import { forwardRef } from 'react';
import styled from '@emotion/styled';
import { Box, Stack } from '@mui/material';
import SvgIcon, { SvgIconProps } from '@mui/material/SvgIcon';

import { IconMap, IconOption, IconOptions, MDIconMap, MuiIconMap } from './iconOptions';

export type IconProps = Omit<
  SvgIconProps,
  'children' | 'classes' | 'inheritViewBox' | 'shapeRendering' | 'viewBox'
> & {
  /**
   * Icon name, specifies which icon will be rendered
   */
  name: IconOption;
  warning?: boolean;
};

const IconBase = forwardRef<SVGSVGElement, IconProps>(({ name, ...rest }, ref) => {
  if (name in MuiIconMap) {
    const Component = IconMap[name];
    return <Component {...rest} ref={ref} />;
  }

  if (name in MDIconMap)
    return (
      <SvgIcon {...rest} ref={ref}>
        <path d={MDIconMap[name as keyof typeof MDIconMap]} />
      </SvgIcon>
    );

  return null;
});

IconBase.displayName = 'IconBase';

const ExclamationMark = styled(IconBase)`
  font-size: 12px;
`;

export const Icon = forwardRef<SVGSVGElement, IconProps>(({ warning = false, ...rest }, ref) =>
  warning ? (
    <Stack alignItems='center' direction='row' spacing={-0.5}>
      <IconBase ref={ref} {...rest} />

      <Box>
        <Box mr={-1}>
          <ExclamationMark color={rest.color} name='exclamationMark' />
        </Box>
      </Box>
    </Stack>
  ) : (
    <IconBase {...rest} ref={ref} />
  )
);

Icon.displayName = 'IconWithWarning';

export { IconOptions };
export type { IconOption };
